import { useSyncExternalStore } from "react";

/**
 * App look. "classic" is the original design; "modern" layers the glass /
 * rounded design on top of the same palette via `html[data-theme="modern"]`
 * (see index.css and the `modern:` Tailwind variant).
 */
export type AppTheme = "classic" | "modern";

export const APP_THEMES: AppTheme[] = ["classic", "modern"];

const STORAGE_KEY = "nn-theme";
const listeners = new Set<() => void>();

function readStoredTheme(): AppTheme {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === "modern" || value === "classic") return value;
  } catch {
    // Storage unavailable — fall back to the default look.
  }
  return "classic";
}

let currentTheme: AppTheme = readStoredTheme();

function applyToDocument(theme: AppTheme) {
  document.documentElement.dataset.theme = theme;
}

/** Call once before the first render so there is no flash of the wrong theme. */
export function initTheme() {
  applyToDocument(currentTheme);
}

export function setTheme(theme: AppTheme) {
  if (theme === currentTheme) return;
  currentTheme = theme;
  try {
    localStorage.setItem(STORAGE_KEY, theme);
  } catch {
    // Not persisted, but still applied for this session.
  }
  applyToDocument(theme);
  listeners.forEach((listener) => listener());
}

export function getTheme(): AppTheme {
  return currentTheme;
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useTheme(): AppTheme {
  return useSyncExternalStore(subscribe, getTheme, getTheme);
}
